import { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Caption1,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  Subtitle1,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { AddRegular, MoreHorizontalRegular, RocketRegular } from '@fluentui/react-icons';

import { ToastContext } from '@shared/contexts';

import {
  AddTaskDialog,
  PlanReadinessPanel,
  PlanSummaryRow,
  TaskDetailsDialog,
  TaskGroupList,
} from '../components/plan';
import { getChangeV2AssessmentPath, getChangeV2ExecutionPath } from '../helpers';
import { TAddTaskInput, useChangeV2Store } from '../state/ChangeV2Store';
import { activeTasks, getPlanReadiness, isRecordReadOnly } from '../utils';

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalL,
    flexWrap: 'wrap',
  },
  headLeft: { display: 'flex', flexDirection: 'column', gap: '2px' },
  muted: { color: tokens.colorNeutralForeground3 },
  actions: { display: 'flex', gap: tokens.spacingHorizontalS, flexWrap: 'wrap' },
});

// Screen 7 - Implementation plan. Converts the approved assessment scope into a
// practical, controlled execution plan and freezes it into a baseline.
const PlanPage = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { changeId } = useParams();
  const { show } = useContext(ToastContext);
  const { getChange, ensurePlan, addPlanTask, startExecution, role, signedBaseline } = useChangeV2Store();

  const [openTaskId, setOpenTaskId] = useState<string | undefined>();
  const [addTaskOpen, setAddTaskOpen] = useState(false);

  const change = changeId ? getChange(changeId) : undefined;

  // Build the draft plan from the approved assessment when planning begins.
  useEffect(() => {
    if (change && !change.plan && change.assessment?.baseline) ensurePlan(change.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [change?.id, change?.plan, change?.assessment?.baseline]);

  if (!change) return null;

  if (!change.assessment?.baseline && !change.plan) {
    return (
      <MessageBar intent="info">
        <MessageBarBody>The implementation plan is created after the assessment is approved.</MessageBarBody>
      </MessageBar>
    );
  }

  const plan = change.plan;
  if (!plan) return null;

  const frozen = Boolean(plan.baseline);
  const readOnly = frozen || isRecordReadOnly(role, change, signedBaseline);
  const active = activeTasks(plan);
  const readiness = getPlanReadiness(plan);
  const readyCount = active.filter(
    (task) => task.required && task.owner && task.dueDate && task.completionCriteria?.trim()
  ).length;

  const openTask = plan.tasks.find((task) => task.id === openTaskId);

  const handleAddTask = (input: TAddTaskInput) => {
    addPlanTask(change.id, input);
    show('success', 'Task added.');
  };

  const handleStartExecution = () => {
    startExecution(change.id);
    show('success', 'Plan frozen and execution started.');
    navigate(getChangeV2ExecutionPath(change.id));
  };

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <div className={classes.headLeft}>
          <Subtitle1>Implementation plan</Subtitle1>
          <Caption1 className={classes.muted}>
            Stage 5 of 9 · {active.length} tasks · {readyCount} ready for execution
          </Caption1>
        </div>
        {!readOnly ? (
          <div className={classes.actions}>
            <Button appearance="primary" icon={<AddRegular />} onClick={() => setAddTaskOpen(true)}>
              Add task
            </Button>
            <Menu positioning="below-end">
              <MenuTrigger disableButtonEnhancement>
                <Button appearance="subtle" icon={<MoreHorizontalRegular />} aria-label="More actions" />
              </MenuTrigger>
              <MenuPopover>
                <MenuList>
                  <MenuItem onClick={() => navigate(getChangeV2AssessmentPath(change.id))}>
                    View approved assessment
                  </MenuItem>
                  <MenuItem
                    onClick={() => show('info', 'A single draft plan version exists. History begins at execution.')}
                  >
                    View plan history
                  </MenuItem>
                </MenuList>
              </MenuPopover>
            </Menu>
          </div>
        ) : null}
      </div>

      {frozen ? (
        <MessageBar intent="success">
          <MessageBarBody>
            Plan version {plan.baseline?.version} is frozen and execution has started. This plan is now read-only.
          </MessageBarBody>
          <MessageBarActions>
            <Button
              appearance="transparent"
              icon={<RocketRegular />}
              onClick={() => navigate(getChangeV2ExecutionPath(change.id))}
            >
              Open execution
            </Button>
          </MessageBarActions>
        </MessageBar>
      ) : null}

      <PlanSummaryRow plan={plan} />

      {!frozen && active.length > 0 ? (
        <PlanReadinessPanel plan={plan} disabled={!readiness.ready} onStartExecution={handleStartExecution} />
      ) : null}

      <TaskGroupList change={change} tasks={plan.tasks} mode="plan" readOnly={readOnly} onOpenTask={setOpenTaskId} />

      <TaskDetailsDialog
        change={change}
        task={openTask}
        mode="plan"
        readOnly={readOnly}
        onClose={() => setOpenTaskId(undefined)}
      />

      <AddTaskDialog plan={plan} open={addTaskOpen} onClose={() => setAddTaskOpen(false)} onSubmit={handleAddTask} />
    </div>
  );
};

export default PlanPage;
